<div class="ledger-form">
	<div class="ledger-form-title">
		<mat-icon><i class="fa-solid fa-book" aria-hidden="true"></i></mat-icon>
		<span>Utwórz wpis w Kasie</span>
	</div>

	<div class="ledger-form-grid">
		<mat-form-field appearance="outline" class="ledger-field ledger-field--wide">
			<mat-label>Opis</mat-label>
			<textarea
				matInput
				rows="2"
				[formControl]="form.controls.description"
				[maxlength]="maxDescriptionLength"
				placeholder="Za co jest ten koszt?"
			></textarea>
		</mat-form-field>

		<mat-form-field appearance="outline" class="ledger-field ledger-field--wide">
			<mat-label>Kategoria</mat-label>
			<mat-select [formControl]="form.controls.categoryId">
				@for (category of categories(); track category.id) {
					<mat-option [value]="category.id">{{ category.name }}</mat-option>
				}
			</mat-select>
		</mat-form-field>

		<mat-form-field appearance="outline" class="ledger-field ledger-field--amount">
			<mat-label>Kwota</mat-label>
			<input
				matInput
				type="number"
				inputmode="decimal"
				min="0"
				step="0.01"
				[formControl]="form.controls.amount"
			/>
		</mat-form-field>

		<mat-form-field appearance="outline" class="ledger-field ledger-field--currency">
			<mat-label>Waluta</mat-label>
			<mat-select [formControl]="form.controls.currency">
				@for (option of currencies; track option.value) {
					<mat-option [value]="option.value">{{ option.value }}</mat-option>
				}
			</mat-select>
		</mat-form-field>

		<mat-form-field appearance="outline" class="ledger-field ledger-field--wide">
			<mat-label>Nr dokumentu</mat-label>
			<input
				matInput
				type="text"
				[formControl]="form.controls.documentNumber"
				[maxlength]="maxDocumentNumberLength"
				placeholder="Numer z faktury / paragonu (odczytany z OCR)"
				autocomplete="off"
			/>
			<mat-hint>Odczytane ze skanu — popraw, jeśli trzeba.</mat-hint>
		</mat-form-field>
	</div>

	@if (duplicateEntry(); as dup) {
		<p class="ledger-hint ledger-hint--warn" role="status">
			<mat-icon><i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i></mat-icon>
			<span
				>Ten numer dokumentu jest już zapisany u tego kierowcy (wpis #{{ dup.id }} z {{ dup.date }}). Możesz
				mimo to dodać wpis.</span
			>
		</p>
	}

	@if (!hasKnownDriver()) {
		@if (suggestionVehicle(); as vehicle) {
			<p class="ledger-hint ledger-hint--info">
				<mat-icon><i class="fa-solid fa-bus" aria-hidden="true"></i></mat-icon>
				<span
					>Podstawiony pojazd: <strong>{{ vehicle.vehicleName }}</strong> ({{
						vehicle.registrationNumber
					}})</span
				>
			</p>
		}

		@if (suggestedDrivers().length === 1) {
			<p class="ledger-hint ledger-hint--info">
				<mat-icon><i class="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i></mat-icon>
				<span>Podpowiedziano kierowcę na podstawie wydarzenia „{{ suggestedDrivers()[0].eventName }}".</span>
			</p>
			@if (suggestedDrivers()[0].statusBlocksAssignment) {
				<p class="ledger-hint ledger-hint--warn">
					Uwaga: status kierowcy ({{ suggestedDrivers()[0].status }}) zwykle nie pozwala na udział w
					wydarzeniu transportowym.
				</p>
			}
		} @else if (suggestedDrivers().length > 1) {
			<div class="ledger-suggestions" role="group" aria-label="Sugerowani kierowcy">
				<span class="ledger-field-label">Wybierz sugerowanego kierowcę:</span>
				<div class="ledger-suggestion-chips">
					@for (
						suggestion of suggestedDrivers();
						track suggestion.driverId + suggestion.eventId + suggestion.stage
					) {
						<button
							type="button"
							mat-stroked-button
							class="ledger-suggestion-chip"
							(click)="pickSuggestion(suggestion)"
							[matTooltip]="
								suggestion.statusBlocksAssignment
									? 'Status kierowcy (' +
										suggestion.status +
										') zwykle nie pozwala na udział w wydarzeniu transportowym.'
									: suggestion.eventName + ' · ' + stageLabels[suggestion.stage]
							"
						>
							@if (suggestion.statusBlocksAssignment) {
								<mat-icon class="ledger-suggestion-warn"
									><i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i
								></mat-icon>
							}
							<span class="ledger-suggestion-name">{{ suggestion.name }} {{ suggestion.surname }}</span>
							<span class="ledger-suggestion-context"
								>{{ suggestion.eventName }} · {{ stageLabels[suggestion.stage] }}</span
							>
						</button>
					}
				</div>
			</div>
		} @else if (suggestionReason() === 'no_driver_assignment') {
			<p class="ledger-hint ledger-hint--warn">
				Znaleziono pojazd i wydarzenie, ale brak przypisanego kierowcy w tym czasie — wybierz ręcznie.
			</p>
		} @else if (suggestionReason() === 'no_vehicle_assignment') {
			<p class="ledger-hint ledger-hint--warn">
				Brak podstawionego pojazdu dla tego skanu o tej godzinie — wybierz kierowcę ręcznie.
			</p>
		} @else if (suggestionReason() === 'no_event') {
			<p class="ledger-hint ledger-hint--warn">
				Pojazd znaleziony, ale w tym czasie nie był przypisany do żadnego wydarzenia — wybierz kierowcę ręcznie.
			</p>
		}

		<mat-form-field appearance="outline" class="ledger-field ledger-field--wide">
			<mat-label>Kierowca</mat-label>
			<input
				matInput
				type="text"
				[formControl]="ledgerDriver"
				[matAutocomplete]="driversAuto"
				placeholder="Zacznij wpisywać imię lub nazwisko"
				autocomplete="off"
			/>
			<mat-autocomplete #driversAuto [displayWith]="displayDriver">
				@for (driver of drivers(); track driver.id) {
					<mat-option [value]="driver">{{ driver.name }} {{ driver.surname }}</mat-option>
				}
			</mat-autocomplete>
		</mat-form-field>
	}

	<div class="ledger-target">
		<span class="ledger-field-label">Waluta raportowa</span>
		<mat-button-toggle-group
			[formControl]="targetCurrency"
			class="ledger-target-group"
			hideSingleSelectionIndicator
		>
			<mat-button-toggle value="PLN">PLN</mat-button-toggle>
			<mat-button-toggle value="EUR">EUR</mat-button-toggle>
		</mat-button-toggle-group>
	</div>

	@if (summary(); as info) {
		<p class="ledger-hint">
			Zostanie utworzony wpis na
			<strong>-{{ info.amount | number: '1.2-2' }} {{ info.currency }}</strong>
			@if (info.categoryName) {
				· {{ info.categoryName }}
			}
		</p>
	}

	@if (error(); as err) {
		<p class="ledger-hint ledger-hint--error" role="alert">{{ err }}</p>
	}

	<div class="ledger-form-actions">
		<button mat-button type="button" (click)="cancel()">Anuluj</button>
		<button mat-flat-button color="primary" type="button" (click)="submit()" [disabled]="saving()">
			@if (saving()) {
				<mat-spinner diameter="18"></mat-spinner>
			} @else {
				<mat-icon><i class="fa-solid fa-book" aria-hidden="true"></i></mat-icon>
			}
			<span>Dodaj do kasy</span>
		</button>
	</div>
</div>
